import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, type Track, type TrackState } from '../api.js';
import StateBadge from '../components/StateBadge.js';
import RepoBadge from '../components/RepoBadge.js';

const GROUPS: { title: string; states: TrackState[] }[] = [
  { title: 'Spec review', states: ['SPEC_REVIEW'] },
  { title: 'Awaiting prereqs', states: ['AWAITING_PREREQS'] },
  { title: 'Queued', states: ['QUEUED'] },
  { title: 'Building / verifying', states: ['BUILDING', 'VERIFYING', 'BLOCKED_HUMAN'] },
  { title: 'PR open', states: ['PR_OPEN'] },
  { title: 'Failed', states: ['FAILED_BUILD', 'FAILED_SCOPE'] },
  { title: 'Done', states: ['MERGED', 'DONE'] },
  { title: 'Drafting / Cancelled', states: ['DRAFTING_SPEC', 'CANCELLED'] },
];

export default function Queue() {
  const [tracks, setTracks] = useState<Track[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.listTracks()
      .then((r) => setTracks(r.tracks))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="empty">Loading…</div>;
  if (error) return <div className="errors">{error}</div>;

  const byState = new Map<TrackState, Track[]>();
  for (const t of tracks) {
    const arr = byState.get(t.state) ?? [];
    arr.push(t);
    byState.set(t.state, arr);
  }

  if (tracks.length === 0) {
    return <div className="empty">No tracks yet — paste a spec to get started.</div>;
  }

  return (
    <div>
      {GROUPS.map((g) => {
        const items = g.states.flatMap((s) => byState.get(s) ?? []);
        if (items.length === 0) return null;
        return (
          <div className="group" key={g.title}>
            <div className="group-title">{g.title}</div>
            {items.map((t) => (
              <Link key={t.id} to={`/tracks/${t.id}`} className="card">
                <StateBadge state={t.state} />
                <div className="card-main">
                  <div className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <RepoBadge targetRepo={t.spec.target_repo} />
                    <span>{t.title}</span>
                  </div>
                  <div className="card-sub">{t.one_line}</div>
                </div>
                {t.blocked_by.length > 0 && (
                  <span className="badge badge-yellow">blocked × {t.blocked_by.length}</span>
                )}
              </Link>
            ))}
          </div>
        );
      })}
    </div>
  );
}
